import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calculator, TrendingUp, Users, CalendarCheck, DollarSign } from 'lucide-react';
import SEO from '../components/SEO';
import { TRADE_LIST, ROI_DEFAULTS } from '../data/tradeData';
import { openBookingModal } from '../components/BookingModal';

const ROICalculator = () => {
  const [step, setStep] = useState(1);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [avgJobValue, setAvgJobValue] = useState('');
  const [desiredJobs, setDesiredJobs] = useState('');
  const [results, setResults] = useState(null);
  const resultsRef = useRef(null);

  const handleTradeSelect = (trade) => {
    setSelectedTrade(trade);
    setAvgJobValue(trade.avgJobValue.toString());
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const calculateROI = () => {
    const jobVal = parseFloat(avgJobValue) || 0;
    const jobs = parseFloat(desiredJobs) || 0;
    if (jobVal <= 0 || jobs <= 0) return;

    const trade = selectedTrade;
    const leadToEstimate = trade?.benchmarks?.leadToEstimate || ROI_DEFAULTS.leadToEstimate;
    const estimateToClose = trade?.benchmarks?.estimateToClose || ROI_DEFAULTS.estimateToClose;

    const leadsNeeded = Math.ceil(jobs / estimateToClose / leadToEstimate);
    const bookedEstimates = Math.ceil(leadsNeeded * leadToEstimate);
    const monthlyRevenue = jobs * jobVal;
    const annualRevenue = monthlyRevenue * 12;

    setResults({
      leadsNeeded,
      bookedEstimates,
      closedJobs: jobs,
      monthlyRevenue,
      annualRevenue,
      tradeName: trade?.name || 'Home Service',
    });
    setStep(3);

    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const resetCalculator = () => {
    setStep(1);
    setSelectedTrade(null);
    setAvgJobValue('');
    setDesiredJobs('');
    setResults(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatCurrency = (num) => {
    if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `$${(num / 1000).toFixed(0)}K`;
    return `$${num.toLocaleString()}`;
  };

  return (
    <div className="roi-calc-page" data-testid="roi-calculator-page">
      <SEO
        title="ROI Calculator | See Your Projected Revenue"
        description="Calculate how many exclusive leads and booked estimates your home service business needs to hit your revenue goals. Free ROI calculator from Skye AI."
        keywords="home service ROI calculator, lead generation calculator, HVAC ROI, roofing lead calculator"
        path="/roi-calculator"
      />

      {/* Hero */}
      <section className="roi-hero">
        <div className="roi-hero__bg" />
        <div className="container">
          <div className="roi-hero__content">
            <div className="roi-hero__badge">
              <Calculator size={16} />
              <span>ROI Calculator</span>
            </div>
            <h1 data-testid="roi-hero-title">See How Much Revenue<br /><span className="gradient-text">We Can Generate for You</span></h1>
            <p>Pick your trade, enter your numbers, and see your projected growth in 30 seconds.</p>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section className="roi-calculator">
        <div className="container">
          {/* Progress Bar */}
          <div className="roi-progress" data-testid="roi-progress">
            <div className="roi-progress__bar">
              <div className="roi-progress__fill" style={{ width: `${(step / 3) * 100}%` }} />
            </div>
            <div className="roi-progress__steps">
              <span className={step >= 1 ? 'active' : ''}>Your Trade</span>
              <span className={step >= 2 ? 'active' : ''}>Your Numbers</span>
              <span className={step >= 3 ? 'active' : ''}>Your Results</span>
            </div>
          </div>

          {/* Step 1: Trade Selection */}
          {step === 1 && (
            <div className="roi-step" data-testid="roi-step-1">
              <h2>What trade are you in?</h2>
              <p className="roi-step__sub">Select your trade so we can tailor the projections.</p>
              <div className="roi-trades-grid">
                {TRADE_LIST.map((trade) => {
                  const TradeIcon = trade.icon;
                  return (
                    <button
                      key={trade.slug}
                      className="roi-trade-tile"
                      onClick={() => handleTradeSelect(trade)}
                      data-testid={`roi-trade-${trade.slug}`}
                      style={{ '--tile-color': trade.color }}
                    >
                      <TradeIcon size={28} />
                      <span>{trade.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Numbers Input */}
          {step === 2 && (
            <div className="roi-step" data-testid="roi-step-2">
              <h2>Tell us about your {selectedTrade?.name} business</h2>
              <p className="roi-step__sub">We'll use these numbers to project your growth.</p>

              <div className="roi-inputs">
                <div className="roi-input-group">
                  <label htmlFor="avgJobValue">
                    <DollarSign size={16} />
                    What's your average job value?
                  </label>
                  <div className="roi-input-wrap">
                    <span className="roi-input-prefix">$</span>
                    <input
                      id="avgJobValue"
                      type="number"
                      value={avgJobValue}
                      onChange={(e) => setAvgJobValue(e.target.value)}
                      placeholder="e.g. 4500"
                      data-testid="roi-input-job-value"
                    />
                  </div>
                  <span className="roi-input-hint">
                    Average for {selectedTrade?.name}: ${selectedTrade?.avgJobValue?.toLocaleString()}
                  </span>
                </div>

                <div className="roi-input-group">
                  <label htmlFor="desiredJobs">
                    <CalendarCheck size={16} />
                    How many jobs do you want per month?
                  </label>
                  <input
                    id="desiredJobs"
                    type="number"
                    value={desiredJobs}
                    onChange={(e) => setDesiredJobs(e.target.value)}
                    placeholder="e.g. 15"
                    className="roi-full-input"
                    data-testid="roi-input-desired-jobs"
                  />
                  <span className="roi-input-hint">How many closed jobs you want each month</span>
                </div>

                <div className="roi-buttons">
                  <button className="roi-back-btn" onClick={() => setStep(1)}>
                    Back
                  </button>
                  <button
                    className="roi-calc-btn btn-gradient"
                    onClick={calculateROI}
                    disabled={!avgJobValue || !desiredJobs}
                    data-testid="roi-calculate-btn"
                  >
                    Calculate My ROI <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Results */}
          {step === 3 && results && (
            <div className="roi-step roi-results" ref={resultsRef} data-testid="roi-step-3">
              <h2>Your Projected {results.tradeName} Growth</h2>
              <p className="roi-step__sub">Here's what your AI Revenue Engine could deliver each month.</p>

              <div className="roi-results-grid">
                <div className="roi-result-card" data-testid="roi-result-leads">
                  <Users size={24} />
                  <span className="roi-result-value">{results.leadsNeeded}</span>
                  <span className="roi-result-label">Exclusive Leads / Mo</span>
                </div>
                <div className="roi-result-card" data-testid="roi-result-estimates">
                  <CalendarCheck size={24} />
                  <span className="roi-result-value">{results.bookedEstimates}</span>
                  <span className="roi-result-label">Booked Estimates / Mo</span>
                </div>
                <div className="roi-result-card" data-testid="roi-result-jobs">
                  <TrendingUp size={24} />
                  <span className="roi-result-value">{results.closedJobs}</span>
                  <span className="roi-result-label">Closed Jobs / Mo</span>
                </div>
                <div className="roi-result-card roi-result-card--highlight" data-testid="roi-result-revenue">
                  <DollarSign size={24} />
                  <span className="roi-result-value">{formatCurrency(results.monthlyRevenue)}</span>
                  <span className="roi-result-label">Monthly Revenue</span>
                </div>
              </div>

              <div className="roi-annual" data-testid="roi-annual-revenue">
                <span className="roi-annual__label">Projected Annual Revenue</span>
                <span className="roi-annual__value">{formatCurrency(results.annualRevenue)}</span>
              </div>

              <div className="roi-results-cta">
                <button
                  onClick={openBookingModal}
                  className="btn-gradient btn-lg"
                  data-testid="roi-cta-button"
                >
                  Book My AI Growth Planning Call <ArrowRight size={18} />
                </button>
                <button className="roi-reset-btn" onClick={resetCalculator}>
                  Recalculate
                </button>
              </div>

              <p className="roi-disclaimer">
                Projections based on Skye AI campaign benchmarks across home service businesses. 
                Actual results vary based on market, competition, and service area. 
                50+ exclusive leads & 15%+ more booked estimates in 90 days — or you don't pay.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ROICalculator;
